/**
 * The app's icon vocabulary: glyphs, and nothing else. Every icon in the UI
 * comes from here — no bare `mdi-*` strings in components — so that one
 * concept always looks the same everywhere and a glyph can be changed in one
 * place.
 *
 * Values are bare Material Design Icons names ("mdi-foo"). Vuetify is
 * configured with the class-based `mdi` icon set (see plugins/vuetify.js),
 * which adds the `mdi` font-family class itself:
 *   <v-icon :icon="ACTION_ICONS.edit" />
 * Never re-add the `mdi` prefix there — a stray extra class is how icons end
 * up hoverable but invisible. The exception is a plain <i>/<span> that is not
 * a <v-icon>: nothing adds the family class for it, so it has to carry a
 * static `mdi` of its own next to the bound value.
 *
 * Which map?
 *   ACTION_ICONS  something the user does: edit, delete, upload, run_report.
 *   STATUS_ICONS  a state the system is in: success, error, pending, loading.
 *   INFO_ICONS    direction and trend indicators: arrows, up/down markers.
 *   DOMAIN_ICONS  a thing in the domain: organization, platform, tag, report.
 *
 * These carry no labels. A glyph means different things in different places —
 * the same magnifier is "search" in one row and "inspect" in another — so the
 * name belongs to the call site, not to the picture. A small set of recurring
 * row actions (edit, delete, …) live in libs/rowActions.ts, which pairs an
 * icon from here with a default label.
 *
 * Duplicate glyphs are deliberate — see the notes at each pair below. Two
 * names for one glyph is cheaper than one name meaning two things.
 */
export const STATUS_ICONS = {
  success: "mdi-check-circle",
  done: "mdi-check",
  error: "mdi-alert-circle",
  warning: "mdi-alert",
  info: "mdi-information",
  processing: "mdi-cog",
  ready: "mdi-play-circle-outline",
  empty: "mdi-close-circle-outline",
  search: "mdi-magnify",
  sad: "mdi-emoticon-sad-outline",
  thumb_up: "mdi-thumb-up",
  clock: "mdi-clock-outline",
  pending: "mdi-timer-sand",
  paused: "mdi-pause-circle-outline",
  canceled: "mdi-cancel",
  bug: "mdi-bug",
  exclamation: "mdi-exclamation",
  unknown: "mdi-help-circle",
  cogs: "mdi-cogs",
  forward: "mdi-fast-forward",
} as const;

export type StatusIconKind = keyof typeof STATUS_ICONS;

export const ACTION_ICONS = {
  edit: "mdi-square-edit-outline",
  delete: "mdi-trash-can-outline",
  email: "mdi-email",
  email_open: "mdi-email-open",
  copy: "mdi-content-copy",
  download: "mdi-tray-arrow-down",
  details: "mdi-magnify-scan",
  history: "mdi-history",
  show_chart: "mdi-chart-box-outline",
  upload: "mdi-tray-arrow-up",
  yearly_overview: "mdi-calendar-outline",
  lock: "mdi-lock-outline",
  unlink: "mdi-link-off",
  unlock: "mdi-lock-open-variant-outline",
  // Same glyph as `play`, kept separate on purpose: this one means "execute
  // this report", `play` means "switch automatic harvesting on".
  run_report: "mdi-play",
  play: "mdi-play",
  stop: "mdi-stop",
  file_download: "mdi-file-download-outline",
  gamepad: "mdi-gamepad-variant",
  file_excel: "mdi-file-excel-outline",
  file_import: "mdi-file-import-outline",
  // Same glyph as `email_open`, kept separate on purpose: this one means
  // "send a verification e-mail", `email_open` means "this message is read".
  verify: "mdi-email-open-outline",
  open_outside: "mdi-open-in-new",
  open_inside: "mdi-arrow-right-bold-box-outline",
  clear: "mdi-close",
  close_circle: "mdi-close-circle",
  click: "mdi-cursor-default-click-outline",
  chevron_up: "mdi-chevron-up",
  chevron_down: "mdi-chevron-down",
  save: "mdi-content-save",
  reset: "mdi-restore",
  chevron_left: "mdi-chevron-left",
  chevron_right: "mdi-chevron-right",
  chevron_double_down: "mdi-chevron-double-down",
  chevron_double_right: "mdi-chevron-double-right",
  chevron_double_left: "mdi-chevron-double-left",
  save_disc: "mdi-content-save-outline",
  plus: "mdi-plus",
  minus: "mdi-minus",
  plus_circle: "mdi-plus-circle",
  minus_circle: "mdi-minus-circle",
  chart_line: "mdi-chart-line",
  dots: "mdi-dots-horizontal",
  loader: "mdi-loading",
  expand: "mdi-arrow-expand-all",
  file_export: "mdi-file-export",
  visible: "mdi-eye-outline",
  hidden: "mdi-eye-off-outline",
  refresh: "mdi-refresh",
  repeat: "mdi-repeat",
  adjust: "mdi-circle-half-full",
} as const;

export type ActionIconKind = keyof typeof ACTION_ICONS;

export const INFO_ICONS = {
  arrow_up: "mdi-arrow-up",
  arrow_down: "mdi-arrow-down",
  arrow_right: "mdi-arrow-right",
  arrow_left: "mdi-arrow-left",
  arrow_up_circle: "mdi-arrow-up-circle-outline",
  arrow_down_circle: "mdi-arrow-down-circle-outline",
} as const;

export type InfoIconKind = keyof typeof INFO_ICONS;

export const DOMAIN_ICONS = {
  organization: "mdi-bank-outline",
  user: "mdi-account",
  consortium: "mdi-web",
  lightbulb: "mdi-lightbulb",
  user_check: "mdi-account-check-outline",
  user_plus: "mdi-account-plus",
  list: "mdi-format-list-bulleted",
  list_box: "mdi-view-list-outline",
  bars: "mdi-menu",
  dollar: "mdi-currency-usd",
  square: "mdi-square",
  star: "mdi-star",
  check_circle: "mdi-record-circle",
  check_square: "mdi-checkbox-marked",
  check_square_outline: "mdi-checkbox-marked-outline",
  unchecked_circle: "mdi-circle-outline",
  unchecked_square: "mdi-checkbox-blank-outline",
  exchange_alt: "mdi-swap-horizontal",
  annotation: "mdi-note",
  tag: "mdi-tag",
  tags: "mdi-tag-multiple",
  dashboard: "mdi-view-dashboard",
  flag: "mdi-flag-variant-outline",
  hand_right: "mdi-hand-pointing-right",
  map: "mdi-map-outline",
  pen: "mdi-fountain-pen-tip",
  border_all: "mdi-border-all",
  layers: "mdi-layers",
  file_search: "mdi-file-search-outline",
  crown: "mdi-crown",
  database: "mdi-database",
  cloud: "mdi-cloud-download",
  cloud_upload: "mdi-cloud-upload",
  key: "mdi-key",
  sitemap: "mdi-sitemap",
  tools: "mdi-tools",
  access_point: "mdi-access-point",
  all: "mdi-fullscreen",
  ambulance: "mdi-ambulance",
  bell: "mdi-bell",
  bullhorn: "mdi-bullhorn",
  // Same glyph as `consortium`, kept separate on purpose: this one is the
  // language/locale globe, `consortium` marks consortium-wide scope.
  globe: "mdi-web",
  masks: "mdi-drama-masks",
  bell_off: "mdi-bell-off",
  book: "mdi-book-open-page-variant",
  newspaper: "mdi-newspaper-variant-outline",
  question: "mdi-help",
  file: "mdi-file-outline",
  folder: "mdi-folder-outline",
  folder_open: "mdi-folder-open-outline",
  // Same glyph as ACTION_ICONS.yearly_overview, kept separate on purpose:
  // this one is a plain date/calendar marker, that one is a named action.
  calendar: "mdi-calendar-outline",
  clipboard: "mdi-clipboard-outline",
  comment: "mdi-comment-outline",
  book_open: "mdi-book-open-variant",
  table: "mdi-table",
  building: "mdi-office-building",
  registered: "mdi-registered-trademark",
  scroll: "mdi-script-text-outline",
} as const;

export type DomainIconKind = keyof typeof DOMAIN_ICONS;

/**
 * Every glyph in the catalog, as a union of the literal values above. Props
 * that take an icon should be typed `IconName`, never `string`: a plain
 * `string` lets a call site invent an `mdi-*` name that no map here knows
 * about, which is how the vocabulary quietly grows a second glyph for a
 * concept that already has one.
 */
export type IconName =
  | (typeof STATUS_ICONS)[StatusIconKind]
  | (typeof ACTION_ICONS)[ActionIconKind]
  | (typeof INFO_ICONS)[InfoIconKind]
  | (typeof DOMAIN_ICONS)[DomainIconKind];
